import { useAppDispatch, useDrawerState, useAuthState } from '@core/configureStore';
import { AppIntegrationKey, closeDrawer } from '@core/features';
import { Divider, Drawer, Segmented, Space, Typography, message } from 'antd';
import React, { useMemo, useState } from 'react';
import { AppIntegrationStatusBadge, ConnectSquareIntegrationButton } from '..';
import { ConnectQuickBooksIntegrationButton } from '..';
import { ConnectJobberIntegrationButton } from '..';
import { ConnectXeroIntegrationButton } from '..';
import { GLOBAL_THEME_COLOR } from '@constants/theme';
import { MIDDLE_STYLE } from '@constants/responsive';
import { SquareRequestType } from '@core/models/user';
import { heroApiService } from '@services/service-register';
import { fetchSelectedLocation } from '@core/features/business/thunks/fetchSelectedLocation.thunk';
import { useBusinessState } from '@core/configureStore';

const { Text } = Typography;

const SQUARE_REQUEST_TYPE_OPTIONS = [
  { label: 'Customer', value: SquareRequestType.CUSTOMER },
  { label: 'Payment', value: SquareRequestType.PAYMENT },
  { label: 'Appointment', value: SquareRequestType.BOOKING },
];

const REQUEST_TYPE_DESCRIPTIONS: Record<SquareRequestType, string> = {
  [SquareRequestType.CUSTOMER]: 'Send invites to newly created Square customers (DEFAULT).',
  [SquareRequestType.PAYMENT]: 'Send invites to customers who made a recent payment.',
  [SquareRequestType.BOOKING]: 'Send invites to customers after a booked appointment.',
};


type Props = {};

const AppIntegrationDrawer = (props: Props) => {
  const dispatch = useAppDispatch();
  const { appIntegrationDrawer, extraParams } = useDrawerState();
  const { user } = useAuthState();
  const { integration } = useBusinessState();
  const appId = useMemo<AppIntegrationKey>(() => extraParams?.appId, [extraParams]);
  const [updatingRequestType, setUpdatingRequestType] = useState(false);

  const currentRequestType: SquareRequestType =
    (user?.selected_location?.square_request_type as SquareRequestType) ??
    SquareRequestType.CUSTOMER;

  const handleClose = () => {
    dispatch(
      closeDrawer({
        drawerName: 'appIntegrationDrawer',
      })
    );
  };

  const handleRequestTypeChange = async (value: SquareRequestType) => {
    try {
      setUpdatingRequestType(true);
      await heroApiService.updateSquareRequestType(value);
      await dispatch(fetchSelectedLocation());
      message.success('Integration type updated');
    } catch (error) {
      message.error('Failed to update integration type');
    } finally {
      setUpdatingRequestType(false);
    }
  };

  return (
    <Drawer
      width={500}
      placement="right"
      closable={true}
      onClose={handleClose}
      open={appIntegrationDrawer}>
      {appId === 'square' && (
        <div style={{ ...MIDDLE_STYLE, flexDirection: 'column' }}>
          <div
            style={{
              width: 70,
              height: 70,
              padding: '10px',
              border: `1px solid ${GLOBAL_THEME_COLOR.$border_color}`,
              borderRadius: 10,
              boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
            }}>
            <img src="square-inc.png" width={'100%'} height={'100%'} alt="Square" />
          </div>
          <h1 style={{ margin: '20px 0px', color: 'black' }}>Square</h1>
          <div style={{ margin: '10px 0px', ...MIDDLE_STYLE, flexDirection: 'column', gap: 12 }}>
            <AppIntegrationStatusBadge appId="square" />
            <ConnectSquareIntegrationButton />
          </div>
          {integration.square && (
            <div
              style={{
                width: '100%',
                marginTop: 16,
                padding: '16px',
                border: `1px solid ${GLOBAL_THEME_COLOR.$border_color}`,
                borderRadius: 8,
                background: '#fafafa',
              }}>
              <Space direction="vertical" style={{ width: '100%' }} size={10}>
                <Text strong style={{ fontSize: 13 }}>
                  Integration Type
                </Text>
                <Segmented
                  disabled={updatingRequestType}
                  options={SQUARE_REQUEST_TYPE_OPTIONS}
                  value={currentRequestType}
                  onChange={val => handleRequestTypeChange(val as SquareRequestType)}
                  block
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {REQUEST_TYPE_DESCRIPTIONS[currentRequestType]}
                </Text>
              </Space>
            </div>
          )}
          <Divider />
          <p>
            Amazely's app for Square makes it easy for businesses to{' '}
            <span style={{ fontWeight: 'bold' }}>collect Google reviews</span>. The app
            automatically sends review invitations to customers after they make a purchase.
          </p>
          <br />
          <p>
            These invitations can be triggered by transactions placed on both{' '}
            <span style={{ fontWeight: 'bold' }}>Square Point of Sale and Invoices</span>. This
            helps businesses collect more reviews, which can improve their online visibility and
            boost customer engagement.
          </p>
        </div>
      )}
      {appId === 'quickbooks' && (
        <div style={{ ...MIDDLE_STYLE, flexDirection: 'column' }}>
          <div
            style={{
              width: 70,
              height: 70,
              padding: '10px',
              border: `1px solid ${GLOBAL_THEME_COLOR.$border_color}`,
              borderRadius: 10,
              boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
            }}>
            <img src="quickbooks.png" width={'100%'} height={'100%'} alt="QuickBooks" />
          </div>
          <h1 style={{ margin: '20px 0px', color: 'black' }}>QuickBooks</h1>
          <div style={{ margin: '10px 0px', ...MIDDLE_STYLE }}>
            <AppIntegrationStatusBadge style={{ marginRight: 10 }} appId="quickbooks" />
            <ConnectQuickBooksIntegrationButton />
          </div>
          <Divider />
          <p>
            Amazely's app for QuickBooks makes it easy for businesses to{' '}
            <span style={{ fontWeight: 'bold' }}>collect Google reviews</span>. The app
            automatically sends review invitations to customers after they make a purchase.
          </p>
        </div>
      )}
      {appId === 'jobber' && (
        <div style={{ ...MIDDLE_STYLE, flexDirection: 'column' }}>
          <div
            style={{
              width: 70,
              height: 70,
              padding: '10px',
              border: `1px solid ${GLOBAL_THEME_COLOR.$border_color}`,
              borderRadius: 10,
              boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
            }}>
            <img src="jobber.png" width={'100%'} height={'100%'} alt="Jobber" />
          </div>
          <h1 style={{ margin: '20px 0px', color: 'black' }}>Jobber</h1>
          <div style={{ margin: '10px 0px', ...MIDDLE_STYLE }}>
            <AppIntegrationStatusBadge style={{ marginRight: 10 }} appId="jobber" />
            <ConnectJobberIntegrationButton />
          </div>
          <Divider />
          <p>
            Amazely's app for Jobber makes it easy for businesses to{' '}
            <span style={{ fontWeight: 'bold' }}>collect Google reviews</span>. The app
            automatically sends review invitations to customers after they make a purchase.
          </p>
        </div>
      )}
      {appId === 'xero' && (
        <div style={{ ...MIDDLE_STYLE, flexDirection: 'column' }}>
          <div
            style={{
              width: 70,
              height: 70,
              padding: '10px',
              border: `1px solid ${GLOBAL_THEME_COLOR.$border_color}`,
              borderRadius: 10,
              boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
            }}>
            <img src="xero.png" width={'100%'} height={'100%'} alt="Xero" />
          </div>
          <h1 style={{ margin: '20px 0px', color: 'black' }}>Xero</h1>
          <div style={{ margin: '10px 0px', ...MIDDLE_STYLE }}>
            <AppIntegrationStatusBadge style={{ marginRight: 10 }} appId="xero" />
            <ConnectXeroIntegrationButton />
          </div>
          <Divider />
          <p>
            Amazely's app for Xero makes it easy for businesses to{' '}
            <span style={{ fontWeight: 'bold' }}>collect Google reviews</span>. The app
            automatically sends review invitations to customers after they make a purchase.
          </p>
        </div>
      )}
      {appId === 'openDental' && (
        <div style={{ ...MIDDLE_STYLE, flexDirection: 'column' }}>
          <div
            style={{
              width: 70,
              height: 70,
              padding: '10px',
              border: `1px solid ${GLOBAL_THEME_COLOR.$border_color}`,
              borderRadius: 10,
              boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
            }}>
            <img src="open-dental.jpg" width={'100%'} height={'100%'} alt="Square" />
          </div>
          <h1 style={{ margin: '20px 0px', color: 'black' }}>Open Dental</h1>
          <Divider />
          <p>
            Amazely's app for Open Dental makes it easy for dental practices to{' '}
            <span style={{ fontWeight: 'bold' }}>collect Google reviews</span>. The app
            automatically sends review invitations to new patients after their appointments.
          </p>
          <br />
          <p>
            For more details, please refer to this guide{' '}
            <a
              href="https://docs.amazely.co/en/articles/8764162-integrate-with-open-dental"
              target="_blank">
              Open Dental integration guide
            </a>
            . Please send us a message if you would like to setup the integration.
          </p>
        </div>
      )}
    </Drawer>
  );
};

export default AppIntegrationDrawer;
